import type {
  ISQDCActionCreateInput,
  ISQDCEventCreateInput,
  SQDCWorkflowParallelPrepareQuery,
} from "@/graphql";
import type { SQDCWorkflowDefinition } from "./domain";

export type SQDCWorkflowParallelPreparation =
  SQDCWorkflowParallelPrepareQuery["SQDCWorkflowParallelPrepare"];
export type SQDCWorkflowParallelPreparationEntry =
  SQDCWorkflowParallelPreparation["entries"][number];
export type SQDCWorkflowParallelDraftInput = {
  event?: ISQDCEventCreateInput;
  action?: ISQDCActionCreateInput;
};
export type SQDCWorkflowParallelDraft = {
  entry: SQDCWorkflowParallelPreparationEntry;
  input?: SQDCWorkflowParallelDraftInput;
};

export function createSQDCWorkflowParallelDraftInput(entry: SQDCWorkflowParallelPreparationEntry) {
  if (entry.requiresUserInput) return undefined;
  const initialValues = structuredClone(entry.initialValues);
  return entry.nodeType === "EVENT"
    ? { event: initialValues as ISQDCEventCreateInput }
    : { action: initialValues as ISQDCActionCreateInput };
}

export function getSQDCWorkflowParallelDraftProgress(drafts: readonly SQDCWorkflowParallelDraft[]) {
  const completed = drafts.filter((draft) => draft.input != null).length;
  return {
    completed,
    total: drafts.length,
    valid: drafts.length > 0 && completed === drafts.length,
  };
}

export function createSQDCWorkflowParallelCommitEntries(
  drafts: readonly SQDCWorkflowParallelDraft[],
) {
  if (!getSQDCWorkflowParallelDraftProgress(drafts).valid) {
    throw new Error("Parallel workflow drafts are incomplete");
  }
  return drafts.map((draft) => ({
    entryKey: draft.entry.entryKey,
    entryNodeId: draft.entry.entryNodeId,
    event: draft.input?.event,
    action: draft.input?.action,
  }));
}

export function getSQDCWorkflowParallelEntryPath(
  entry: SQDCWorkflowParallelPreparationEntry,
  definition: SQDCWorkflowDefinition,
) {
  return entry.path
    .flatMap(({ parallelNodeId, entryNodeId }) => [parallelNodeId, entryNodeId])
    .filter((nodeId, index, path) => path[index - 1] !== nodeId)
    .map((nodeId) => definition.nodes.find((node) => node.id === nodeId))
    .filter((node) => node != null);
}
